import { useEffect, useMemo, useState } from 'react';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Paper, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi, DialogUI, InputUI, ButtonUI } from '../../Interface';
import productionService from '../../../service/production.service';

const ProductionReturnDialog = ({ open, onClose, selectedLine, prodId, onSave, disabled = false }) => {
  const [issuedBatches, setIssuedBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);

  const selectedLineIndex = selectedLine?.selectedLineIndex ?? 0;
  const itemCode = selectedLine?.itemCode ?? '';

  const getOpenQty = (row) => {
    if (!row) return 0;
    return (
      row.openedQty ??
      row.openQty ??
      row.openedQuantity ??
      row.openQuantity ??
      row.remainingQty ??
      row.remainingQuantity ??
      0
    );
  };

  const getBatchKey = (row) => {
    if (!row) return null;
    return row.batchSnId ?? row.batchId ?? row.id ?? row.batchSn ?? null;
  };

  useEffect(() => {
    if (!open || !prodId || !itemCode) return;
    productionService.getProductionReturnData(prodId, itemCode).then((data) => {
      const rows = Array.isArray(data) ? data : data?.data ?? [];
      setIssuedBatches(rows);
    });
  }, [open, prodId, itemCode]);

  useEffect(() => {
    if (!open) return;
    const preset = (selectedLine?.docLineBatchSn ?? []).map((batch) => {
      const issueMatch = issuedBatches.find((row) => {
        const rowKey = getBatchKey(row);
        return rowKey && rowKey === getBatchKey(batch) ? true : row?.batchSn === batch?.batchSn;
      });
      return {
        ...issueMatch,
        id: batch.id ?? uuidv4(),
        batchSn: batch.batchSn ?? issueMatch?.batchSn ?? '',
        quantity: batch.quantity ?? '',
        openedQty: getOpenQty(issueMatch),
      };
    });
    setSelectedBatches(preset);
  }, [open, selectedLine, issuedBatches]);

  const issuedColumns = dataGridPremiumColumns([
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Szerk.',
      cellClassName: 'actions',
      sortable: false,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<AddIcon />}
          label="Add"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const batchKey = getBatchKey(row);
            if (!batchKey) return;
            setSelectedBatches((prev) => {
              if (prev.some((b) => b.batchKey === batchKey)) return prev;
              const openedQty = getOpenQty(row);
              return [
                ...prev,
                {
                  ...row,
                  id: uuidv4(),
                  batchKey,
                  batchSn: row.batchSn ?? '',
                  quantity: openedQty ?? '',
                  openedQty,
                },
              ];
            });
          }}
          color="inherit"
          disabled={disabled}
        />,
      ],
    },
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      type: 'string',
      sortable: false,
    },
    {
      field: 'openedQty',
      headerName: 'Nyitott mennyiség',
      type: 'number',
      sortable: false,
      valueGetter: (_data, row) => getOpenQty(row),
    },
    {
      field: 'effectiveQty',
      headerName: 'Kiadott mennyiség',
      type: 'number',
      sortable: false,
    },
    {
      field: 'lot',
      headerName: 'LOT',
      type: 'string',
      sortable: false,
    },
  ]);

  const selectedColumns = dataGridPremiumColumns([
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      type: 'string',
      sortable: false,
    },
    {
      field: 'quantity',
      headerName: 'Mennyiség',
      type: 'number',
      sortable: false,
      renderCell: (data) => {
        const rowIndex = selectedBatches.findIndex((b) => b.id === data.id);
        const maxQty = selectedBatches[rowIndex]?.openedQty ?? undefined;
        return (
          <InputUI
            type="number"
            value={selectedBatches[rowIndex]?.quantity ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const parsed = parseFloat(value);
              const bounded = Number.isNaN(parsed)
                ? ''
                : maxQty !== undefined && maxQty !== null
                  ? Math.min(parsed, Number(maxQty))
                  : parsed;
              setSelectedBatches((prev) => prev.map((b, i) => (i === rowIndex ? { ...b, quantity: bounded } : b)));
            }}
            inputProps={{ min: 0, ...(maxQty !== undefined && maxQty !== null ? { max: maxQty } : {}) }}
            fullWidth
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Szerk.',
      cellClassName: 'actions',
      sortable: false,
      getActions: ({ id }) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setSelectedBatches((prev) => prev.filter((b) => b.id !== id));
          }}
          color="inherit"
          disabled={disabled}
        />,
      ],
    },
  ]);

  const totalSelectedQty = useMemo(() => {
    return selectedBatches.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
  }, [selectedBatches]);

  const handleSave = () => {
    const docLineBatchSn = selectedBatches.map((line) => ({
      id: line.id ?? uuidv4(),
      batchSn: line.batchSn ?? '',
      lot: line.lot ?? '',
      supplierId: line.supplierId ?? '',
      expDate: line.expDate ?? null,
      mnfDate: line.mnfDate ?? null,
      batchAttribute1: line.batchAttribute1 ?? '',
      batchAttribute2: line.batchAttribute2 ?? '',
      comment: line.comment ?? '',
      price: line.price ?? '',
      other: line.other ?? '',
      quantity: line.quantity ?? 0,
    }));

    onSave({ docLineBatchSn, totalQty: totalSelectedQty });
    onClose();
  };

  if (!open) return null;

  return (
    <DialogUI open={open} onClose={onClose} headerContent="Visszavét - Sarzs" maxWidth="lg" fullWidth>
      <Box sx={{ overflowX: 'auto' }}>
        <Box className="align-middle">
          <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
              <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Sor: </Typography>
                <Typography>{selectedLineIndex + 1}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Cikkszám: </Typography>
                <Typography>{itemCode}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Nyitott mennyiség: </Typography>
                <Typography>{selectedLine?.qty ?? ''}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Összesített sarzs: </Typography>
                <Typography>{totalSelectedQty}</Typography>
              </Box>
            </Box>

            <Box display="flex" gap={2}>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  datagridIdentifier={DataGridIdentifiers.ProductionReturnIssuedBatchTable}
                  height={480}
                  columns={issuedColumns}
                  rows={issuedBatches ?? []}
                  slots={{
                    toolbar: null,
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  getRowId={(row) => getBatchKey(row) ?? row.batchSn ?? row.id}
                />
              </Box>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  datagridIdentifier={DataGridIdentifiers.ProductionReturnSelectedBatchTable}
                  height={480}
                  columns={selectedColumns}
                  rows={selectedBatches ?? []}
                  slots={{
                    toolbar: null,
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  getRowId={(row) => row.id}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} disabled={disabled} />
        <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default ProductionReturnDialog;
